import {BlockComponentProps} from 'client-react-block-renderer';
import React, {Component} from 'react';
import {getCheckoutOrderManager} from 'cart-ui';
import AddToCartButtonComponent from './AddToCartButtonComponent';
import {setSections as passSectionDataToAdminEditor} from './adminCommunicationSingleton';
import calculateTotals from './calculateTotals';
import createMergedAndRankedSectionData from './createMergedAndRankedSectionData';
import FormattedTotals from './FormattedTotals';
import formatMoney from './lib/httpRemoteNumberFormatting/formatMoney';
import formatNumber from './lib/httpRemoteNumberFormatting/formatNumber';
import PotentialOrderLineDictionary from './PotentialOrderLineDictionary';
import SkuLine from './SkuLine';
import SkuSectionComponent from './SkuSectionComponent';
import Totals from './Totals';
import TotalsComponent from './TotalsComponent';
import { GtagCartUpdateMethod } from 'google-analytics';
import t from 'rcm-i18n';

interface State {
    potentialOrderLines: PotentialOrderLineDictionary;
    totals: Totals;
    formattedTotals: FormattedTotals;
}

type Props = BlockComponentProps<Data, Config>;

interface Config {
    mainHeaderText: string;
    categoryName: string;
    sectionLabelForUnRankedProducts: string;
    sections: any;
}

interface Data {
    skusInCategory: SkuLine[];
    showPrices: boolean;
    showBvPv: boolean;
    formattedMoneyZero: string;
    formattedNumberZero: string;
}

export default class QuickShopBlockComponent extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.handleSkuQuantityChange = this.handleSkuQuantityChange.bind(this);
        this.handleAddToCart = this.handleAddToCart.bind(this);

        if (!props.data || Object.keys(props.data).length === 0) {
            return; // handle case when block dragged on page by admin with no data provider data.
        }
        const data = props.data as Data;

        const potentialOrderLines = {};
        data.skusInCategory.forEach((skuLine) => {
            potentialOrderLines[skuLine.skuNumber] = {
                formattedLineTotal: data.formattedMoneyZero,
                quantity: 0,
            };
        });
        this.state = {
            formattedTotals: {
                bv: data.formattedNumberZero,
                loading: false,
                pv: data.formattedNumberZero,
                subTotal: data.formattedMoneyZero,
            },
            potentialOrderLines,
            totals: {
                bv: 0,
                pv: 0,
                subTotal: 0,
            },
        };
    }

    private handleSkuQuantityChange(skuLine: SkuLine, newQuantity: number) {
        const potentialOrderLines = Object.assign({}, this.state.potentialOrderLines);
        potentialOrderLines[skuLine.skuNumber] = {
            formattedLineTotal: null, // Make the line totals display blank while loading
            quantity: newQuantity,
        };
        this.setState({potentialOrderLines});

        formatMoney(newQuantity * skuLine.price).then(((formattedLineTotal) => {
            // tslint:disable-next-line:no-shadowed-variable
            const potentialOrderLines = Object.assign({}, this.state.potentialOrderLines);
            potentialOrderLines[skuLine.skuNumber] = {
                formattedLineTotal,
                quantity: newQuantity,
            };
            this.setState({potentialOrderLines});
        }));
        const data = this.props.data as Data;

        const newTotals = calculateTotals(data.skusInCategory, potentialOrderLines);

        // Make the totals display blank while loading
        this.setState({formattedTotals: {bv: null, pv: null, subTotal: null, loading: true}});

        const formattingOperations = [
            new Promise((resolve, reject) => {
                formatNumber(newTotals.bv).then((formattedValue) => resolve(formattedValue));
            }),
            new Promise((resolve, reject) => {
                formatNumber(newTotals.pv).then((formattedValue) => resolve(formattedValue));
            }),
            new Promise((resolve, reject) => {
                formatMoney(newTotals.subTotal).then((formattedValue) => resolve(formattedValue));
            }),
        ];

        Promise.all(formattingOperations).then((values: string[]) => {
            const formattedTotals = {
                bv: values[0],
                loading: false,
                pv: values[1],
                subTotal: values[2],
            };
            this.setState({formattedTotals});
        });
    }

    private async handleAddToCart() {
        const items = [];
        // tslint:disable-next-line:forin
        for (const skuNumber in this.state.potentialOrderLines) {
            const quantity = this.state.potentialOrderLines[skuNumber].quantity;
            if (quantity === 0) {
                continue;
            }
            items.push(
                {
                    data: null,
                    qty: quantity,
                    skuNumber,
                    type: null,
                }
            );
        }

        // if (items.length < 1) { //@TODO add error handling!
        //     $scope.error = translateFilter('Please add at least one item.');
        //     rcmLoading.setLoading('RcmQuickShopCtrl', 1);
        //     return;
        // }

        const orderMgr = await getCheckoutOrderManager();
        try {
            await orderMgr.addItems(items, { cart_update_method: GtagCartUpdateMethod.QUICK_SHOP_TOOL });
            orderMgr.redirectToCart();
        } catch (e) {
            alert(t('An error occurred'))
            throw e;
        }
    }

    public render() {
        const config = this.props.config;
        const data = this.props.data as Data;
        if (!data || Object.keys(data).length === 0) {
            // handle case when block dragged on page by admin with no data provider data.
            return <div className="alert alert-warning" role="alert">
                <p><strong>Save the page once to be able to see and edit this block.</strong></p>
                <p>(Technical Details: No data-provider data was given during render)</p>
            </div>;
        }
        if (!data.showPrices) {
            // If show-prices is not allowed, then redirect to login and show nothing.
            window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
            return null;
        }
        const sections = createMergedAndRankedSectionData(
            config.sections, config.sectionLabelForUnRankedProducts, data.skusInCategory
        );
        passSectionDataToAdminEditor(sections);
        return (<div>
            <h1>{config.mainHeaderText}</h1>
            <div className="quick-shop-with-sections-bottom-add-top-cart-button">
                <AddToCartButtonComponent onClick={this.handleAddToCart}/>
            </div>
            <table className="table quick-shop-with-sections-sku-table">
                {sections.map((section, sectionIndex) => <SkuSectionComponent
                    key={sectionIndex}
                    label={section.label}
                    skuLines={section.skuLines}
                    potentialOrderLines={this.state.potentialOrderLines}
                    onSkuQuantityChange={this.handleSkuQuantityChange}
                    showBvPv={data.showBvPv}
                />)}
            </table>
            <div className="clearfix quick-shop-with-sections-totals-component">
                <TotalsComponent formattedTotals={this.state.formattedTotals} showBvPv={data.showBvPv}/>
            </div>
            <div className="clearfix quick-shop-with-sections-bottom-add-to-cart-button">
                <AddToCartButtonComponent onClick={this.handleAddToCart}/>
            </div>
        </div>);
    }
}
